import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { getUnreadCount } from '../services/emails';
import { usePusherEvent } from '../hooks/usePusherChannel';

interface UnreadEmailContextValue {
  unreadCount: number;
  refreshUnreadCount: () => void;
}

const UnreadEmailContext = createContext<UnreadEmailContextValue>({
  unreadCount: 0,
  refreshUnreadCount: () => {},
});

export function UnreadEmailProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);

  const refreshUnreadCount = useCallback(() => {
    getUnreadCount().then(setUnreadCount).catch(() => {});
  }, []);

  // Initial fetch
  useEffect(() => {
    if (user) refreshUnreadCount();
  }, [user, refreshUnreadCount]);

  // Pusher: new email arrived → re-fetch count
  usePusherEvent(
    user?.uid ? `user-${user.uid}` : null,
    'email.new',
    refreshUnreadCount,
  );

  return (
    <UnreadEmailContext.Provider value={{ unreadCount, refreshUnreadCount }}>
      {children}
    </UnreadEmailContext.Provider>
  );
}

export function useUnreadEmail(): UnreadEmailContextValue {
  return useContext(UnreadEmailContext);
}
